import Cookies from "js-cookie";

/**
 * Enhanced utility for handling ad metadata (query string parameters)
 * Optimized for iOS/Safari compatibility with ITP and cross-site restrictions
 * Only runs in production environment
 */

// Detect iOS/Safari for platform-specific optimizations
const getPlatformInfo = () => {
  if (typeof window === 'undefined') return { isIOS: false, isSafari: false };

  const userAgent = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);

  return { isIOS, isSafari };
};

// Store ad metadata (query string) with iOS/Safari optimized fallback
export const storeAdMetadata = (queryString: string): void => {
  if (process.env.APP_ENV !== 'prod' || !queryString) {
    return;
  }

  const { isIOS, isSafari } = getPlatformInfo();

  try {
    // Shorter expiry for iOS/Safari due to ITP restrictions
    const days = isIOS || isSafari ? 7 : 30; // 7 days for iOS/Safari, 30 for others
    const expiry = new Date(Date.now() + days * 24 * 60 * 60 * 1000);

    // Try different cookie strategies for iOS/Safari
    if (isIOS || isSafari) {
      // For iOS/Safari, try SameSite=None with Secure if HTTPS
      if (window.location.protocol === 'https:') {
        try {
          document.cookie = `ad_metadata=${encodeURIComponent(queryString)};path=/;expires=${expiry.toUTCString()};SameSite=None;Secure`;
        } catch (e) {
          // Fallback to Lax if None+Secure fails
          document.cookie = `ad_metadata=${encodeURIComponent(queryString)};path=/;expires=${expiry.toUTCString()};SameSite=Lax`;
        }
      } else {
        // HTTP fallback
        document.cookie = `ad_metadata=${encodeURIComponent(queryString)};path=/;expires=${expiry.toUTCString()};SameSite=Lax`;
      }
    } else {
      // Standard cookie for other browsers
      document.cookie = `ad_metadata=${encodeURIComponent(queryString)};path=/;expires=${expiry.toUTCString()};SameSite=Lax`;
    }

    // Enhanced localStorage fallback (especially important for iOS/Safari)
    try {
      localStorage.setItem('ad_metadata', queryString);
      localStorage.setItem('ad_metadata_expires', (Date.now() + days * 24 * 60 * 60 * 1000).toString());
      localStorage.setItem('ad_metadata_platform', isIOS ? 'ios' : isSafari ? 'safari' : 'other');
      localStorage.setItem('ad_metadata_timestamp', Date.now().toString());
    } catch (e) {
      // iOS Private mode or storage quota exceeded - try sessionStorage
      try {
        sessionStorage.setItem('ad_metadata', queryString);
        sessionStorage.setItem('ad_metadata_platform', isIOS ? 'ios' : isSafari ? 'safari' : 'other');
      } catch (sessionError) {
        // Even sessionStorage failed - complete fallback failure
        console.warn('All storage methods failed for ad metadata');
      }
    }
  } catch (error) {
    // Silent fail - don't break the page
  }
};

// Enhanced retrieval with iOS/Safari specific fallbacks
export const getAdMetadata = (): string | null => {
  if (typeof window === "undefined") return null;

  const { isIOS, isSafari } = getPlatformInfo();

  // Try cookie first
  let adMetadata = Cookies.get("ad_metadata");

  if (!adMetadata) {
    // Enhanced localStorage fallback
    try {
      const expires = localStorage.getItem('ad_metadata_expires');
      if (expires && Date.now() < parseInt(expires)) {
        adMetadata = localStorage.getItem('ad_metadata') || undefined;
      }
    } catch (e) {
      // Try sessionStorage fallback (for iOS private mode)
      try {
        adMetadata = sessionStorage.getItem('ad_metadata') || undefined;
      } catch (sessionError) {
        // Silent fail
      }
    }
  }

  // For iOS/Safari, also check if we need to refresh the storage
  if (adMetadata && (isIOS || isSafari)) {
    try {
      const timestamp = localStorage.getItem('ad_metadata_timestamp');
      if (timestamp) {
        const age = Date.now() - parseInt(timestamp);
        const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days for iOS/Safari

        if (age > maxAge) {
          // Data is too old, clear it
          localStorage.removeItem('ad_metadata');
          localStorage.removeItem('ad_metadata_expires');
          localStorage.removeItem('ad_metadata_timestamp');
          adMetadata = null;
        }
      }
    } catch (e) {
      // Silent fail
    }
  }

  return adMetadata ? decodeURIComponent(adMetadata) : null;
};

// Parse query string into key-value pairs
export const parseAdMetadata = (queryString: string): Record<string, string> => {
  if (!queryString) return {};

  const params = new URLSearchParams(queryString);
  const result: Record<string, string> = {};

  params.forEach((value, key) => {
    result[key] = value;
  });

  return result;
};

// Utility to check if current platform has tracking restrictions
export const hasTrackingRestrictions = (): boolean => {
  const { isIOS, isSafari } = getPlatformInfo();
  return isIOS || isSafari;
};

// Utility to get storage status for debugging
export const getStorageStatus = () => {
  if (typeof window === "undefined") return null;

  const { isIOS, isSafari } = getPlatformInfo();

  return {
    platform: isIOS ? 'ios' : isSafari ? 'safari' : 'other',
    cookieSupported: !!Cookies.get("ad_metadata"),
    localStorageSupported: (() => {
      try {
        localStorage.setItem('test', 'test');
        localStorage.removeItem('test');
        return true;
      } catch (e) {
        return false;
      }
    })(),
    sessionStorageSupported: (() => {
      try {
        sessionStorage.setItem('test', 'test');
        sessionStorage.removeItem('test');
        return true;
      } catch (e) {
        return false;
      }
    })(),
    hasRestrictions: hasTrackingRestrictions(),
    adMetadataFound: !!getAdMetadata()
  };
};
